import React, { useEffect, useState, useContext, useCallback } from 'react';
import { AuthContext } from '../../context/AuthContext';
import api from '../../services/api.js';
import './Dashboard.css';

import CoursesView from './components/CoursesView';
import ModulesGridView from './components/ModulesGridView';
import ModuleDetailView from './components/ModuleDetailView';

export default function Dashboard() {
  const { user } = useContext(AuthContext);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [progressDataMap, setProgressDataMap] = useState({}); // Mapa de courseId -> progressData // Armazenará o progresso de um curso específico
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalModules: 0,
    completedLessons: 0,
    totalLessons: 0,
    progressPercentage: 0,
  });

  const loadCourses = useCallback(async () => {
    try {
      // O backend agora retorna apenas cursos matriculados com módulos e aulas incluídos.
      const res = await api.get('/courses/my-courses');
      const userCourses = res.data || [];
      setCourses(userCourses);

      // Carrega o progresso de todos os cursos matriculados
      const progressPromises = userCourses.map(course => loadProgress(course.id));
      await Promise.all(progressPromises);
    } catch (err) {
      console.error('Erro ao carregar cursos matriculados', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  useEffect(() => {
    const calculateStats = () => {
      if (!courses.length) return;

      const totalModules = courses.flatMap(c => c.modules || []).length;
      const allLessons = courses.flatMap(
        course => course.modules?.flatMap(m => m.lessons || []) || []
      );
      const totalLessons = allLessons.length;

      // Calculate total completed lessons across all courses
      let totalCompletedLessons = 0;
      let totalProgressPercentage = 0;
      let courseCount = 0;

      courses.forEach(course => {
        const progressData = progressDataMap[course.id];
        if (progressData) {
          const completedLessons =
            progressData.modules
              ?.flatMap(m => m.lessons)
              .filter(l => l.progress.status === 'COMPLETED').length || 0;
          totalCompletedLessons += completedLessons;
          totalProgressPercentage += progressData.progressPercent || 0;
          courseCount++;
        }
      });

      const averageProgress = courseCount > 0 ? totalProgressPercentage / courseCount : 0;

      setStats({
        totalModules,
        completedLessons: totalCompletedLessons,
        totalLessons,
        progressPercentage: Math.round(averageProgress),
      });
    };

    calculateStats();
  }, [courses, progressDataMap]);

  async function loadProgress(courseId) {
    if (!courseId) return;
    try {
      // A rota GET /api/progress retorna o progresso completo de um curso
      const res = await api.get(`/progress?courseId=${courseId}`);
      setProgressDataMap(prev => ({
        ...prev,
        [courseId]: res.data || null,
      }));
    } catch (err) {
      // Se a API retornar 404 (progresso não encontrado), definimos como nulo para não quebrar a UI.
      if (err.response && err.response.status === 404) {
        setProgressDataMap(prev => ({
          ...prev,
          [courseId]: null,
        }));
        return;
      }
      console.error('Erro ao carregar progresso', err);
    }
  }

  const markLessonAsCompleted = async lessonId => {
    // Usa o selectedModule que já tem as aulas e o courseId.
    // Se não houver um módulo selecionado, não faz nada.
    if (!selectedModule || !selectedModule.courseId) return;

    try {
      await api.post('/progress/lessons/complete', { lessonId });
      loadProgress(selectedModule.courseId); // Recarrega o progresso do curso atual para atualizar a UI
    } catch (err) {
      console.error('Erro ao marcar como visto', err);
    }
  };

  const unmarkLessonAsCompleted = async lessonId => {
    // Usa o selectedModule que já tem as aulas e o courseId.
    // Se não houver um módulo selecionado, não faz nada.
    if (!selectedModule || !selectedModule.courseId) return;

    try {
      await api.post('/progress/lessons/uncomplete', { lessonId });
      loadProgress(selectedModule.courseId); // Recarrega o progresso do curso atual para atualizar a UI
    } catch (err) {
      console.error('Erro ao desmarcar como visto', err);
    }
  };

  const isLessonCompleted = (lessonId, courseId) => {
    if (!courseId || !progressDataMap[courseId]) return false;
    const progressData = progressDataMap[courseId];
    const lesson = progressData.modules?.flatMap(m => m.lessons).find(l => l.id === lessonId);
    return lesson?.progress?.status === 'COMPLETED';
  };

  const getModuleProgress = moduleId => {
    // Encontra o módulo em qualquer um dos cursos
    const course = courses.find(c => c.modules?.some(m => m.id === moduleId));
    const module = course?.modules?.find(m => m.id === moduleId);
    if (!module || !course) return 0;

    const totalLessons = (module.lessons || []).filter(l => !l.isOptional).length;
    const completedLessons = (module.lessons || []).filter(lesson =>
      isLessonCompleted(lesson.id, course.id)
    ).length;

    return totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;
  };

  const getCourseProgress = courseId => {
    // Calcula o progresso específico de um curso
    if (!courseId || !progressDataMap[courseId]) return 0;

    const progressData = progressDataMap[courseId];
    return Math.round(progressData.progressPercent || 0);
  };

  const getYouTubeVideoId = url => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);

    return match && match[2].length === 11 ? match[2] : null;
  };

  const getModuleThumbnail = module => {
    if (!module || !module.lessons || module.lessons.length === 0) {
      return null;
    }

    // Encontra a primeira aula que tenha conteúdo de vídeo (YouTube)
    const firstVideoLesson = module.lessons.find(
      lesson =>
        lesson.content &&
        (lesson.content.includes('youtube.com') || lesson.content.includes('youtu.be'))
    );

    if (!firstVideoLesson || !firstVideoLesson.content) {
      return null;
    }

    // Extrai o ID do vídeo do YouTube da URL
    const videoId = getYouTubeVideoId(firstVideoLesson.content);
    return videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : null;
  };

  const handleSelectModule = module => {
    // O módulo já vem completo da API, basta setá-lo no estado.
    setSelectedModule(module);
    window.scrollTo(0, 0);
  };

  const handleSelectCourse = course => {
    setSelectedCourse(course);
    window.scrollTo(0, 0);
  };

  const handleBackFromModules = () => {
    setSelectedCourse(null);
    window.scrollTo(0, 0);
  };

  let content;
  if (selectedModule) {
    content = (
      <ModuleDetailView
        module={selectedModule}
        getModuleProgress={getModuleProgress}
        onBack={() => {
          setSelectedModule(null);
          window.scrollTo(0, 0);
        }}
        viewed={lessonId => isLessonCompleted(lessonId, selectedModule.courseId)}
        markViewed={markLessonAsCompleted}
        unmarkViewed={unmarkLessonAsCompleted}
      />
    );
  } else if (selectedCourse) {
    content = (
      <ModulesGridView
        course={selectedCourse}
        modules={selectedCourse.modules || []}
        getModuleProgress={getModuleProgress}
        getModuleThumbnail={getModuleThumbnail}
        setSelectedModule={handleSelectModule}
        onBack={handleBackFromModules}
      />
    );
  } else {
    content = (
      <CoursesView
        user={user}
        stats={stats}
        courses={courses}
        loading={loading}
        getModuleProgress={getModuleProgress}
        getModuleThumbnail={getModuleThumbnail}
        getCourseProgress={getCourseProgress}
        setSelectedCourse={handleSelectCourse}
      />
    );
  }

  return (
    <div className="dashboard">
      <div className="container">{content}</div>
    </div>
  ); // Main dashboard view
}
